import { useCallback, useRef, type MutableRefObject, type Ref } from 'react';

/**
 * Bridges a forwarded ref with an internal handle to the same node.
 *
 * Returns a stable ref callback to attach to the element plus the internal
 * `MutableRefObject` the component can read in effects. Both object and
 * callback forwarded refs are honored, so callers keep their ref while the
 * component still gets to reach the DOM node on its own.
 *
 * @example
 * const [setRef, nodeRef] = useForwardedRef(forwardedRef);
 */
export const useForwardedRef = <T,>(
  forwardedRef: Ref<T>
): [(node: T | null) => void, MutableRefObject<T | null>] => {
  const nodeRef = useRef<T | null>(null);

  const setRef = useCallback(
    (node: T | null) => {
      nodeRef.current = node;
      if (typeof forwardedRef === 'function') {
        forwardedRef(node);
      } else if (forwardedRef) {
        (forwardedRef as MutableRefObject<T | null>).current = node;
      }
    },
    [forwardedRef]
  );

  return [setRef, nodeRef];
};

export default useForwardedRef;
